import { tv } from 'tailwind-variants';
import { useSnapshot } from 'valtio';

import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';
import TrimBar from '@cartwhl/ui/organisms/TrimBar';

import { sceneSettingsStore, uiStore } from 'stores';
import { AnimationPlayerStoreInstance } from 'stores/animationPlayerStoreFactory';

const trimPrimaryAction = tv({
  base: '',
  variants: {
    inline: {
      true: 'flex h-full items-center justify-end',
      false: 'flex h-full flex-none items-center',
    },
  },
  defaultVariants: { inline: false },
});

export default function TrimControls({
  animationPlayerStore,
  inline,
  showPlayButton = true,
}: {
  animationPlayerStore: AnimationPlayerStoreInstance;
  inline?: boolean;
  showPlayButton?: boolean;
}) {
  const { isPlaying, duration, currentFrame, trimFrameStart, trimFrameEnd } =
    useSnapshot(animationPlayerStore.state);

  const {
    backgroundSettings: { hexColor },
    playbackSettings: { frameRate },
  } = useSnapshot(sceneSettingsStore.state);
  const { promptFocus } = useSnapshot(uiStore.state);

  const originalFps = duration.originalFrames / duration.originalSeconds;
  const convertToOriginalFrame = (fpsAdjustedFrame: number) => {
    return Math.round((fpsAdjustedFrame / frameRate) * originalFps);
  };
  const convertToFpsAdjustedFrame = (originalFrame: number) => {
    return Math.round((originalFrame / originalFps) * frameRate);
  };

  const primaryAction = inline ? (
    <>
      {showPlayButton && (
        <GoButton
          icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
          onClick={() => animationPlayerStore.playOrPause()}
        />
      )}
    </>
  ) : (
    <Button
      variant="secondary"
      onClick={() => animationPlayerStore.setControls('player')}
    >
      Done
    </Button>
  );
  const trimArea = inline ? (
    <>
      {/* 
        Inside trimbar we use original frames, while outside we use fps adjusted frames... 
        This means we need to convert all fps adjusted frames back to original frames and vice verca
        */}
      <TrimBar
        value={convertToOriginalFrame(currentFrame)}
        onValueChange={(frame: number) => {
          animationPlayerStore.setFrame(convertToFpsAdjustedFrame(frame));
        }}
        trimStart={trimFrameStart || 0}
        trimEnd={trimFrameEnd || duration.originalFrames}
        onTrimChange={(s, e) => {
          animationPlayerStore.setTrimFrames([s, e]);
        }}
        maxValue={duration.originalFrames}
        format={(frame) => `${Math.round(frame)}`}
        bgColor={hexColor}
        hideHandleInfo
      />
      <Button
        variant="secondary"
        onClick={() => animationPlayerStore.setControls('player')}
      >
        Done
      </Button>
    </>
  ) : (
    <>
      {showPlayButton && (
        <GoButton
          active={!promptFocus}
          icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
          onClick={() => animationPlayerStore.playOrPause()}
        />
      )}
      {/* 
        Inside trimbar we use original frames, while outside we use fps adjusted frames... 
        This means we need to convert all fps adjusted frames back to original frames and vice verca
        */}
      <TrimBar
        value={convertToOriginalFrame(currentFrame)}
        onValueChange={(frame: number) => {
          animationPlayerStore.setFrame(convertToFpsAdjustedFrame(frame));
        }}
        trimStart={trimFrameStart || 0}
        trimEnd={trimFrameEnd || duration.originalFrames}
        onTrimChange={(s, e) => animationPlayerStore.setTrimFrames([s, e])}
        maxValue={duration.originalFrames}
        format={(frame) => `${Math.round(frame)}`}
        bgColor={hexColor}
        hideHandleInfo
      />
    </>
  );
  return (
    <div className="pointer-events-auto flex w-full gap-3">
      <div className={trimPrimaryAction({ inline })}>{primaryAction}</div>
      <div className="flex h-full flex-1 items-center gap-3">{trimArea}</div>
    </div>
  );
}
